import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import ProgressBar from "../ProgressBar"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { MediaQuery, TERMINAL_MAX_WARNING } from "../../../utils/design-helpers"
import { DataPlanModel } from "../../../utils/models"
import CardBar from "../../atoms/CardBar"

import H4 from "../../atoms/typography/H4"
import { ArrowDropDownIcon } from "../../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../../atoms/icons/ArrowDropUpIcon"
import AddDataButton from "../../atoms/buttons/AddDataButton"
import TextButton from "../../atoms/buttons/TextButton"
import BodyMedium from "../../atoms/typography/BodyMedium"
import Body from "../../atoms/typography/Body"
import dayjs from "dayjs"

interface Props {
  dataplan: DataPlanModel
  onClickAddData: () => void
  onClickStatistics: () => void
  statisticsOpen: boolean
}

const ProgressBarSection = styled.div`
  width: 60%;
`

const ProgressBarcolumn = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;

  ${MediaQuery.XS} {
    justify-content: flex-start;
  }
`

const DataPlanOverviewBar: React.FC<Props> = ({ dataplan, onClickAddData, onClickStatistics, statisticsOpen }) => {
  const { t } = useTranslation()

  const [subActive, setSubActive] = React.useState<boolean>(true)

  React.useMemo(() => {
    const dateParts = dataplan.actionBarProps.validDates?.split(" - ")
    const latestSubscriptionDate = dateParts?.[1] ?? undefined

    const today = new Date()

    setSubActive(dayjs(latestSubscriptionDate ?? today).isAfter(dayjs(today)))
  }, [subActive])

  return (
    <CardBar open>
      <Row align="center" justify="between">
        <Col lg={2} style={{ overflow: "clip" }}>
          <Body color={theme.colors.white}>{dataplan.actionBarProps.suborganisation}</Body>
          <H4 color={theme.colors.white}>{dataplan.name}</H4>
          <Visible xs md>
            <HorizontalSpacer spacing={1} />
          </Visible>
        </Col>
        <Col lg={2}>
          <Hidden xs md>
            <Body color={theme.colors.white} textAlign="center">
              {t("subscription")}:
            </Body>
            <BodyMedium color={theme.colors.white} textAlign="center">
              {dataplan.actionBarProps.validDates}
            </BodyMedium>
          </Hidden>
        </Col>
        <ProgressBarcolumn lg={5}>
          <ProgressBarSection>
            <Hidden xs md>
              <Body color={theme.colors.white} textAlign="center">
                {t("data_usage")}:
              </Body>
              <HorizontalSpacer spacing={0.5} />
            </Hidden>
            <ProgressBar
              warningMax={TERMINAL_MAX_WARNING}
              currentProgress={dataplan.actionBarProps.currentProgress ?? 0}
            />
            <HorizontalSpacer spacing={0.5} />

            <BodyMedium color={theme.colors.white} textAlign="center">
              {dataplan.actionBarProps.dataUsage}
            </BodyMedium>
          </ProgressBarSection>
          <Spacer spacing={1} />
          <AddDataButton
            title={t("add_data")}
            onClick={onClickAddData}
            disabled={dataplan.actionBarProps.status !== "active" || dataplan.sharedPlan || !subActive}
          />
        </ProgressBarcolumn>
        <Hidden xs md>
          <Col
            lg={2}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <TextButton
              title={t("statistics")}
              color={theme.colors.white}
              icon={
                statisticsOpen ? (
                  <ArrowDropUpIcon color={theme.colors.white} />
                ) : (
                  <ArrowDropDownIcon color={theme.colors.white} />
                )
              }
              onClick={onClickStatistics}
            />
          </Col>
        </Hidden>
      </Row>
      <Visible xs md>
        <Row justify="center" onClick={onClickStatistics}>
          {statisticsOpen ? (
            <ArrowDropUpIcon color={theme.colors.white} />
          ) : (
            <ArrowDropDownIcon color={theme.colors.white} />
          )}
        </Row>
      </Visible>
    </CardBar>
  )
}

export default DataPlanOverviewBar
